import { Alert, Button } from '@recipes/ui';
import { Camera, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from '@/components/feedback/toaster';
import { useCatalogBoot, useShoppingAt } from '@/features/catalog/hooks';
import { ShoppingAtControl } from '@/features/catalog/shopping-at';
import { TripPhotoQueueFullError, startReceiptScan } from '@/features/prices/trip-photos';
import { useAiConsent, useConfig, useFeature } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { toastError } from '@/lib/errors';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';

interface Part {
  key: string;
  file: File;
  url: string;
}

/**
 * Photograph a receipt (docs/partial/shopping-in-store-spec.md 5.4): one photo, or several for a
 * long one – top part first, until the total is visible. The photos can be taken without a
 * connection; reading them needs the server and the AI the user consented to.
 */
export function ReceiptCapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useCatalogBoot();
  const [params] = useSearchParams();
  const listId = params.get('listId');
  const online = useOnlineStatus();
  const consented = useAiConsent();
  const aiFeature = useFeature('ai');
  const receiptFeature = useFeature('receipt_scan');
  const { data: config } = useConfig();
  const { store } = useShoppingAt();
  const maxPhotos = config?.prices?.receiptMaxImages ?? 6;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [sending, setSending] = useState(false);

  // Object URLs live as long as the page shows the photo.
  const urls = useRef<string[]>([]);
  useEffect(() => () => urls.current.forEach((url) => URL.revokeObjectURL(url)), []);

  const add = (files: FileList | null) => {
    const picked = [...(files ?? [])].slice(0, Math.max(0, maxPhotos - parts.length));
    if (inputRef.current) inputRef.current.value = '';
    if (!picked.length) return;
    setParts((current) => [
      ...current,
      ...picked.map((file) => {
        const url = URL.createObjectURL(file);
        urls.current.push(url);
        return { key: crypto.randomUUID(), file, url };
      }),
    ]);
  };

  const blocked = useMemo(() => {
    if (!receiptFeature || !aiFeature) return t('receipts.capture.disabled');
    if (!consented) return t('receipts.capture.needsAi');
    return null;
  }, [aiFeature, consented, receiptFeature, t]);

  const submit = async () => {
    if (!parts.length) return;
    setSending(true);
    try {
      const started = await startReceiptScan({
        files: parts.map((part) => part.file),
        shoppingListId: listId,
        storeId: store?.id ?? null,
      });
      if (started.queued) {
        toast.show({ title: t('receipts.capture.queued') });
        navigate('/shopping/receipts', { replace: true });
      } else navigate(`/shopping/receipts/${started.result.id}`, { replace: true });
    } catch (error) {
      if (error instanceof TripPhotoQueueFullError) toast.error(t('prices.tag.queueFull'));
      else toastError(error);
    } finally {
      setSending(false);
    }
  };

  return (
    <PageContainer className="max-w-xl">
      <FeaturePageHeader
        title={t('receipts.capture.title')}
        description={t('receipts.capture.description')}
        backTo={listId ? `/shopping/${listId}` : '/shopping/receipts'}
      />
      {blocked && (
        <Alert variant="warning" className="mb-4" data-testid="receipt-blocked">
          {blocked}
        </Alert>
      )}
      {!blocked && !online && <Alert className="mb-4">{t('receipts.capture.offline')}</Alert>}

      <div className="mb-4">
        <ShoppingAtControl />
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(event) => add(event.target.files)}
        data-testid="receipt-input"
      />

      {parts.length > 0 && (
        <ol className="mb-4 grid grid-cols-3 gap-2" data-testid="receipt-parts">
          {parts.map((part, index) => (
            <li key={part.key} className="relative overflow-hidden rounded-md border">
              <img
                src={part.url}
                alt={t('receipts.capture.part', { number: index + 1 })}
                className="aspect-[3/4] w-full object-cover"
              />
              <span className="absolute left-1 top-1 rounded bg-background/90 px-1.5 text-xs font-medium">
                {index + 1}
              </span>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-1 top-1 size-8"
                aria-label={t('receipts.capture.removePart', { number: index + 1 })}
                onClick={() => setParts((current) => current.filter((p) => p.key !== part.key))}
              >
                <Trash2 aria-hidden />
              </Button>
            </li>
          ))}
        </ol>
      )}

      <div className="space-y-2">
        <Button
          variant={parts.length ? 'outline' : 'default'}
          className="h-12 w-full"
          disabled={Boolean(blocked) || parts.length >= maxPhotos || sending}
          onClick={() => inputRef.current?.click()}
          data-testid="receipt-add-photo"
        >
          <Camera aria-hidden />
          {parts.length ? t('receipts.capture.nextPart') : t('receipts.capture.first')}
        </Button>
        {parts.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('receipts.capture.untilTotal', { count: parts.length, max: maxPhotos })}
          </p>
        )}
        <Button
          className="h-12 w-full"
          disabled={Boolean(blocked) || parts.length === 0}
          loading={sending}
          onClick={() => void submit()}
          data-testid="receipt-submit"
        >
          {t('receipts.capture.read')}
        </Button>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">{t('receipts.capture.privacy')}</p>
    </PageContainer>
  );
}
